// Club link URL rules, shared by components/AddLinkSheet.js (app) and the web
// link editors (web/src/components/clubs/LinkEditor.jsx, manage/LinkModal.jsx).
export const URL_MAX_LEN = 500;
export const NAME_MAX_LEN = 80;

// Pragmatic "has a dot-separated host, no spaces" check — not a full RFC
// URL validator. Requires an http(s) scheme (auto-prefixed below when
// missing), a host segment (up to the first "/") containing at least one
// dot, and no whitespace anywhere in the string.
// Any scheme-like prefix (e.g. "javascript:", "ftp:") — used to REJECT
// non-http(s) schemes outright instead of blindly prefixing them with
// https:// (which would smuggle "javascript:x.com" through as
// "https://javascript:x.com").
// (?![0-9]) — a colon followed by digits is a PORT ("example.com:8443"),
// not a scheme; without the lookahead that input would be misclassified
// as a non-HTTP scheme and rejected instead of auto-prefixed.
const ANY_SCHEME_REGEX = /^[a-zA-Z][a-zA-Z0-9+.-]*:(?![0-9])/;
const HAS_SCHEME_REGEX = /^https?:\/\//i;
// Dotted hostname of valid DNS label characters (no userinfo, no raw IP
// niceties needed for club links). DOCUMENTED LIMITATION: ASCII hostnames
// only — Unicode IDNs and trailing-dot FQDNs are rejected; club links are
// ASCII platform domains in practice, and IDNA handling isn't worth the
// dependency.
const HOSTNAME_REGEX =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

// Parse the authority section of an http(s) URL into { hostname } or null.
// Rejects: userinfo ("user@host" tricks), non-numeric ports
// ("x.com:bad", "javascript:alert" authorities), undotted hosts.
function parseHostname(withScheme) {
  const m = withScheme.match(/^https?:\/\/([^/?#]+)/i);
  if (!m) return null;
  const authority = m[1];
  if (authority.includes("@")) return null;
  let host = authority;
  const colon = authority.indexOf(":");
  if (colon !== -1) {
    const port = authority.slice(colon + 1);
    if (!/^\d{1,5}$/.test(port) || Number(port) > 65535) return null;
    host = authority.slice(0, colon);
  }
  host = host.toLowerCase();
  return HOSTNAME_REGEX.test(host) ? { hostname: host } : null;
}

// Trim -> auto-prefix a missing scheme -> validate. Pure function so both
// the live disabled-state check and the Save handler use the identical
// result.
export function normalizeAndValidateUrl(rawUrl) {
  const trimmed = (rawUrl || "").trim();
  if (!trimmed || /\s/.test(trimmed)) return { normalized: "", valid: false };
  // A non-http(s) scheme is an outright rejection, never auto-prefixed.
  if (ANY_SCHEME_REGEX.test(trimmed) && !HAS_SCHEME_REGEX.test(trimmed)) {
    return { normalized: trimmed, valid: false };
  }
  // Canonicalize the scheme to lowercase ("HTTPS://X.COM" would otherwise
  // be stored uppercase and break case-sensitive startsWith("http")
  // consumers downstream). The rest of the URL keeps its case.
  const withScheme = HAS_SCHEME_REGEX.test(trimmed)
    ? trimmed.replace(/^https?:\/\//i, (m) => m.toLowerCase())
    : `https://${trimmed}`;
  return { normalized: withScheme, valid: parseHostname(withScheme) !== null };
}
